import { useEffect, useRef, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { VideoPlayer } from "../components/VideoPlayer";
import { VideoEditModal } from "../components/VideoEditModal";
import { FilmSlate, UploadSimple, LinkSimple, PlayCircle, Trash, Sparkle , PencilSimple } from "@phosphor-icons/react";
import { DepartmentPicker } from "../components/DepartmentPicker";

const ROLE_TAGS = ["all", "server", "bartender", "barista", "line_cook", "housekeeping"];
const CATEGORIES = ["Training", "Kitchen", "Bar", "Service", "Housekeeping", "Safety"];

export default function MediaStudioPage() {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [editVideo, setEditVideo] = useState(null);
  const [mode, setMode] = useState("link");
  const [form, setForm] = useState({ title: "", description: "", role_tag: "all", category: "Training", url: "" });
  const [deptIds, setDeptIds] = useState([]);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [stats, setStats] = useState({});
  const fileRef = useRef(null);

  const load = () => {
    api.get("/videos").then((r) => setVideos(r.data));
    api.get("/videos/watch-stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (mode === "link" && !form.url.trim()) return toast.error("Paste a video link (YouTube, Vimeo or direct .mp4)");
    if (mode === "upload" && !file) return toast.error("Choose a video file");
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, mode === "upload" && k === "url" ? "" : v));
      fd.append("department_ids", deptIds.join(","));
      if (mode === "upload" && file) fd.append("file", file);
      await api.post("/videos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Video published — AI quiz generated");
      setForm({ title: "", description: "", role_tag: "all", category: "Training", url: "" });
      setDeptIds([]);
      setFile(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/videos/${id}`); toast.success("Video removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-8 rise-in" data-testid="media-studio-page">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">
          {user.role === "developer" ? "Universal Academy Studio" : "Media & Video Studio"}
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Video Studio</h1>
        <p className="text-slate-500 mt-3">
          {user.role === "developer"
            ? "Manage the Universal Academy library — these videos are visible to every brand on the platform."
            : "Upload master training videos or attach links. Each video is tagged by role and gets an auto-generated 2-question quiz."}
        </p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-4">
        <div className="flex gap-2">
          <button data-testid="media-mode-link" onClick={() => setMode("link")}
            className={`h-11 px-5 rounded-full text-sm font-bold flex items-center gap-2 tap-press border ${mode === "link" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}>
            <LinkSimple size={18} /> Video link
          </button>
          <button data-testid="media-mode-upload" onClick={() => setMode("upload")}
            className={`h-11 px-5 rounded-full text-sm font-bold flex items-center gap-2 tap-press border ${mode === "upload" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600"}`}>
            <UploadSimple size={18} /> Upload file
          </button>
        </div>
        <p className="text-xs font-semibold text-slate-400">Tip: external video links are the most reliable source — large uploads can fail on slow networks.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="media-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Video title (e.g. Espresso Dial-In Basics)" className="h-12 px-4 rounded-xl border border-slate-200" />
          {mode === "link" ? (
            <input data-testid="media-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
              placeholder="YouTube / Vimeo / direct .mp4 link" className="h-12 px-4 rounded-xl border border-slate-200" />
          ) : (
            <button data-testid="media-file-button" onClick={() => fileRef.current?.click()}
              className="h-12 px-4 rounded-xl border-2 border-dashed border-slate-300 text-sm font-bold text-slate-500 tap-press hover:border-slate-900 truncate">
              {file ? file.name : "Choose .mp4 / .webm / .mov"}
            </button>
          )}
          <input ref={fileRef} type="file" accept=".mp4,.webm,.mov" className="hidden" data-testid="media-file-input"
            onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <select data-testid="media-role-select" value={form.role_tag} onChange={(e) => setForm({ ...form, role_tag: e.target.value })}
            className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold capitalize">
            {ROLE_TAGS.map((r) => <option key={r} value={r}>{r === "all" ? "All roles" : r.replace("_", " ")}</option>)}
          </select>
          <select data-testid="media-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <textarea data-testid="media-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Short description — the AI uses this to build the quiz" rows={2}
          className="w-full px-4 py-3 rounded-xl border border-slate-200 resize-none" />
        {user.role !== "developer" && <div className="mb-4"><DepartmentPicker value={deptIds} onChange={setDeptIds} testId="media-dept-picker" /></div>}
        <button data-testid="media-submit-button" onClick={submit} disabled={busy}
          className="h-14 px-8 rounded-full bg-slate-900 text-white font-heading font-bold flex items-center gap-2 tap-press hover:bg-slate-800 disabled:opacity-50">
          {busy ? <><Sparkle size={20} className="pulse-dot" /> Publishing & generating quiz…</> : <><FilmSlate size={20} weight="duotone" /> Publish video</>}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="media-video-grid">
        {videos.map((v) => (
          <div key={v.id} data-testid={`media-video-card-${v.id}`}
            className="bg-white border border-slate-200 rounded-2xl overflow-hidden card-lift">
            <button onClick={() => setPlaying(v)} data-testid={`media-play-${v.id}`}
              className="relative w-full h-40 bg-slate-900 flex items-center justify-center tap-press group">
              <PlayCircle size={56} weight="duotone" className="text-white/90 group-hover:scale-110 transition-transform" />
              <span className="absolute bottom-3 left-3 text-[11px] font-bold text-white/70 uppercase tracking-wider">
                {v.source_type === "upload" ? "Uploaded file" : "Linked video"}
              </span>
            </button>
            <div className="p-5">
              <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-emerald-600">{v.category} · {v.role_tag === "all" ? "All roles" : v.role_tag.replace("_", " ")}</span>
              <h3 className="font-heading font-bold text-lg mt-1 leading-snug">{v.title}</h3>
              {v.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{v.description}</p>}
              <div className="flex items-center justify-between mt-4">
                <span className="text-xs text-slate-400 font-semibold" data-testid={`media-watch-count-${v.id}`}>
                  {v.quiz?.length || 0} quiz Qs · {stats[v.id]?.completed_count || 0} completed views
                </span>
                {["admin", "developer"].includes(user.role) && (
                  <button data-testid={`media-delete-${v.id}`} onClick={() => remove(v.id)}
                    className="p-2 text-slate-300 hover:text-red-500 tap-press"><Trash size={18} /></button>
                )}
                {(["admin", "developer"].includes(user.role) || true) && (
                  <button data-testid={`media-edit-video-${v.id}`} onClick={() => setEditVideo(v)}
                    className="p-2 text-slate-300 hover:text-slate-900 tap-press"><PencilSimple size={18} /></button>
                )}
              </div>
            </div>
          </div>
        ))}
        {videos.length === 0 && <p className="text-slate-400 col-span-full">No training videos yet — publish your first one above.</p>}
      </div>

      {playing && (
        <VideoPlayer video={playing} onClose={() => setPlaying(null)}>
          {playing.quiz?.length > 0 && (
            <div className="mt-5 pt-5 border-t border-slate-100">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">Auto-generated quiz</p>
              {playing.quiz.map((q, i) => (
                <div key={q.id} className="mb-4">
                  <p className="font-bold text-sm">{i + 1}. {q.question}</p>
                  <div className="mt-2 grid sm:grid-cols-2 gap-2">
                    {q.options.map((o, j) => (
                      <span key={j} className={`text-sm px-3 py-2 rounded-lg border ${j === q.correct_index ? "border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold" : "border-slate-100 bg-slate-50 text-slate-600"}`}>{o}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {stats[playing.id]?.watchers?.length > 0 && (
            <div className="mt-5 pt-5 border-t border-slate-100" data-testid="media-watchers-list">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-3">View audit</p>
              <div className="space-y-2">
                {stats[playing.id].watchers.map((w, i) => (
                  <div key={i} className="flex items-center justify-between text-sm font-semibold">
                    <span>{w.user_name}</span>
                    <span className={w.completed ? "text-emerald-600" : "text-slate-400"}>
                      {w.max_pct}%{w.completed ? " · completed" : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </VideoPlayer>
      )}
      {editVideo && <VideoEditModal video={editVideo} onClose={() => setEditVideo(null)} onSaved={() => load()} />}
    </div>
  );
}
